// Automation wie in Pro Tools (Reference Guide Kap. 49): Kurven je Spur für Lautstärke, Panorama, Mute und Sends.
// Reine Funktionen auf Track-Daten, damit Bearbeiten, Wiedergabe und Schreiben dieselbe Logik nutzen.
import type { AutoMode, AutoPoint, Project, Track } from '@srs/shared';
import { pluginDef, resolveParams, type ParamDef } from '../audio/plugins.ts';

export interface AutoParam { id: string; label: string; min: number; max: number; unit: string; step?: boolean; choices?: boolean; def?: ParamDef; group?: string }
export const AUTO_MODES: { id: AutoMode; label: string; hint: string }[] = [
  { id: 'off', label: 'Off', hint: 'Automation wird ignoriert' },
  { id: 'read', label: 'Read', hint: 'Spielt die Kurven ab' },
  { id: 'touch', label: 'Touch', hint: 'Schreibt, solange ihr den Regler haltet, danach wieder Read' },
  { id: 'latch', label: 'Latch', hint: 'Schreibt ab dem ersten Anfassen bis zum Stopp' },
  { id: 'write', label: 'Write', hint: 'Schreibt Lautstärke und Panorama vom Start bis zum Stopp, danach Touch' },
];
export function autoParams(busses: { id: string; name: string }[], t?: Track): AutoParam[] {
  const base: AutoParam[] = t?.id === 'master' ? [{ id: 'volume', label: 'Lautstärke', min: -60, max: 6, unit: 'dB' }] : [
    { id: 'volume', label: 'Lautstärke', min: -60, max: 6, unit: 'dB' },
    { id: 'pan', label: 'Panorama', min: -1, max: 1, unit: '' },
    { id: 'mute', label: 'Mute', min: 0, max: 1, unit: '', step: true },
    ...busses.map((b) => ({ id: `send:${b.id}`, label: `Send ${b.name}`, min: -60, max: 6, unit: 'dB' })),
  ];
  // Parameter der eingebauten Plugins in den Inserts (Bridge-Plugins haben hier keine Parameter)
  for (const s of t?.inserts ?? []) {
    const def = pluginDef(s.plugin);
    if (!def) continue;
    for (const d of def.params) base.push(insParam(s.id, def.short, d));
  }
  return base;
}
const insParam = (slotId: string, short: string, d: ParamDef): AutoParam => ({ id: `ins:${slotId}:${d.key}`, label: `${short}: ${d.label}`, min: d.min, max: d.max, unit: d.unit ?? '', step: !!d.bool, choices: !!d.choices, def: d, group: short });
/** Plugin-Parameter: 'ins:<slotId>:<key>' */
export const isInsParam = (id: string) => id.startsWith('ins:');
export function paramInfo(id: string, t?: Track): AutoParam {
  if (isInsParam(id)) {
    const [, slotId, key] = id.split(':');
    const s = t?.inserts.find((x) => x.id === slotId), def = s && pluginDef(s.plugin), d = def?.params.find((x) => x.key === key);
    return d && def ? insParam(slotId, def.short, d) : { id, label: key ?? id, min: -1e6, max: 1e6, unit: '' };
  }
  return id === 'pan' ? { id, label: 'Panorama', min: -1, max: 1, unit: '' } : id === 'mute' ? { id, label: 'Mute', min: 0, max: 1, unit: '', step: true } : { id, label: id === 'volume' ? 'Lautstärke' : `Send ${id.slice(5)}`, min: -60, max: 6, unit: 'dB' };
}
export const clampV = (param: string, v: number, t?: Track) => {
  const p = paramInfo(param, t), x = Math.max(p.min, Math.min(p.max, v));
  return p.step ? (x >= 0.5 ? 1 : 0) : p.choices ? Math.round(x) : Math.round(x * 1000) / 1000;
};

/** Master als spurähnliches Objekt: Lautstärke = Master-Fader, Automation = masterAutomation */
export function masterTrack(p: Project): Track {
  return {
    id: 'master', name: 'Master', color: '#071026', kind: 'bus', armed: false, mute: false, solo: false, pan: 0, sends: [],
    get inserts() { return p.masterInserts ?? []; },
    get volumeDb() { return p.masterDb; }, set volumeDb(v: number) { p.masterDb = v; },
    get automation() { return p.masterAutomation; }, set automation(a) { p.masterAutomation = a; },
  } as Track;
}
/** Automatisierte Plugin-Parameter einer Spur am Zeitpunkt pos: slotId → { key: Wert } (ohne die gerade von Hand gesteuerten) */
export function insOverrides(t: Track, pos: number, manual?: Set<string>): Record<string, Record<string, number>> | undefined {
  if (!t.automation || t.automation.mode === 'off') return undefined;
  let out: Record<string, Record<string, number>> | undefined;
  for (const [param, pts] of Object.entries(t.automation.lanes)) {
    if (!pts.length || !isInsParam(param) || manual?.has(`${t.id}|${param}`)) continue;
    const [, slotId, key] = param.split(':');
    ((out ??= {})[slotId] ??= {})[key] = clampV(param, valueAt(pts, pos, param, 0), t);
  }
  return out;
}
/** Spur oder Master nach Id */
export const trackLike = (p: Project, id: string): Track | undefined => (id === 'master' ? masterTrack(p) : p.tracks.find((t) => t.id === id));

/** Wert ohne Automation (Fader, Pan-Regler, Mute-Taste, Send-Pegel) */
export function staticValue(t: Track, param: string): number {
  if (param === 'volume') return t.volumeDb;
  if (param === 'pan') return t.pan;
  if (param === 'mute') return t.mute ? 1 : 0;
  if (param.startsWith('send:')) { const l = t.sends.find((s) => s.busId === param.slice(5))?.level ?? 0; return l > 0 ? Math.max(-60, 20 * Math.log10(l)) : -60; }
  if (isInsParam(param)) {
    const [, slotId, key] = param.split(':');
    const s = t.inserts.find((x) => x.id === slotId), def = s && pluginDef(s.plugin);
    return def ? resolveParams(def, s!.params)[key] ?? 0 : 0;
  }
  return 0;
}

export const points = (t: Track, param: string): AutoPoint[] => t.automation?.lanes[param] ?? [];
/** Hat die Spur für diesen Parameter Punkte und ist nicht auf Off? */
export const isAutomated = (t: Track, param: string) => (t.automation?.mode ?? 'off') !== 'off' && points(t, param).length > 0;

/** Wert zur Zeit t: linear zwischen Punkten, Mute springt; vor dem ersten und nach dem letzten Punkt konstant */
export function valueAt(pts: AutoPoint[], t: number, param: string, fallback: number): number {
  if (!pts.length) return fallback;
  if (t <= pts[0].t) return pts[0].v;
  if (t >= pts[pts.length - 1].t) return pts[pts.length - 1].v;
  let lo = 0, hi = pts.length - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (pts[m].t <= t) lo = m; else hi = m; }
  const a = pts[lo], b = pts[hi];
  if (param === 'mute' || b.t - a.t < 1e-9) return a.v;
  return a.v + ((b.v - a.v) * (t - a.t)) / (b.t - a.t);
}

function lanes(t: Track) {
  t.automation ??= { mode: 'read', lanes: {} };
  return t.automation.lanes;
}
const sort = (pts: AutoPoint[]) => pts.sort((a, b) => a.t - b.t);

/** Punkt setzen. Bei leerer Kurve kommt ein Startpunkt mit dem bisherigen Wert dazu, damit vorher nichts springt. */
export function addPoint(t: Track, param: string, at: number, v: number) {
  const L = lanes(t);
  const pts = (L[param] ??= []);
  if (!pts.length && at > 0.001) pts.push({ t: 0, v: staticValue(t, param) });
  const same = pts.find((p) => Math.abs(p.t - at) < 0.0005);
  if (same) same.v = clampV(param, v, t); else pts.push({ t: Math.max(0, at), v: clampV(param, v, t) });
  sort(pts);
  if (t.automation!.mode === 'off') t.automation!.mode = 'read';
}
export function movePoint(t: Track, param: string, idx: number, at: number, v: number) {
  const pts = points(t, param);
  if (!pts[idx]) return;
  const lo = idx > 0 ? pts[idx - 1].t + 0.0005 : 0, hi = idx < pts.length - 1 ? pts[idx + 1].t - 0.0005 : Infinity;
  pts[idx] = { t: Math.max(lo, Math.min(hi, at)), v: clampV(param, v, t) };
}
export function deletePoint(t: Track, param: string, idx: number) {
  const pts = points(t, param);
  pts.splice(idx, 1);
}

/** Punkte im Bereich löschen. edges: Randpunkte setzen, damit sich die Kurve außerhalb nicht ändert. */
export function removeRange(t: Track, param: string, s: number, e: number, edges: boolean) {
  const pts = points(t, param);
  if (!pts.length) return;
  const vs = valueAt(pts, s, param, 0), ve = valueAt(pts, e, param, 0);
  const keep = pts.filter((p) => p.t < s - 1e-6 || p.t > e + 1e-6);
  if (edges) { keep.push({ t: s, v: vs }, { t: e, v: ve }); }
  lanes(t)[param] = sort(keep);
}

/** Alle Punkte ab `at` verschieben (Insert Time, Insert Silence, Shuffle). Negativ: vorher den Bereich entfernen. */
export function shiftAfter(t: Track, at: number, delta: number) {
  if (!t.automation) return;
  for (const [param, pts] of Object.entries(t.automation.lanes)) {
    if (delta > 0) {
      const v = valueAt(pts, at, param, 0);
      const moved = pts.map((p) => (p.t >= at - 1e-6 ? { ...p, t: p.t + delta } : p));
      // Der Wert an der Einfügestelle bleibt während der eingefügten Zeit stehen
      if (pts.some((p) => p.t < at - 1e-6) && pts.some((p) => p.t >= at - 1e-6)) moved.push({ t: at, v }, { t: at + delta, v });
      t.automation.lanes[param] = sort(moved);
    } else t.automation.lanes[param] = pts.map((p) => (p.t >= at - 1e-6 ? { ...p, t: Math.max(0, p.t + delta) } : p));
  }
}

/** Zeit herausschneiden: Punkte in [s, e) weg, dahinter rückt nach */
export function cutTimeAuto(t: Track, s: number, e: number) {
  if (!t.automation) return;
  for (const param of Object.keys(t.automation.lanes)) removeRange(t, param, s, e, true);
  for (const [param, pts] of Object.entries(t.automation.lanes)) {
    const out = pts.filter((p) => p.t < s - 1e-6 || p.t > e - 1e-6).map((p) => (p.t >= e - 1e-6 ? { ...p, t: p.t - (e - s) } : p));
    t.automation.lanes[param] = dedupe(out);
  }
}
const dedupe = (pts: AutoPoint[]) => sort(pts).filter((p, i, a) => i === 0 || Math.abs(p.t - a[i - 1].t) > 1e-6 || p.v !== a[i - 1].v);

/** Automation eines Bereichs kopieren (relative Zeiten, mit Randpunkten) */
export function copyAuto(t: Track, s: number, e: number): Record<string, AutoPoint[]> | undefined {
  if (!t.automation) return undefined;
  const out: Record<string, AutoPoint[]> = {};
  for (const [param, pts] of Object.entries(t.automation.lanes)) {
    if (!pts.length) continue;
    out[param] = [{ t: 0, v: valueAt(pts, s, param, 0) }, ...pts.filter((p) => p.t > s + 1e-6 && p.t < e - 1e-6).map((p) => ({ t: p.t - s, v: p.v })), { t: e - s, v: valueAt(pts, e - 1e-6, param, 0) }];
  }
  return Object.keys(out).length ? out : undefined;
}
/** Kopierte Automation an `at` einsetzen (ersetzt, was dort lag) */
export function pasteAuto(t: Track, data: Record<string, AutoPoint[]>, at: number, len: number) {
  for (const [param, pts] of Object.entries(data)) {
    const had = points(t, param).length > 0;
    if (had) removeRange(t, param, at, at + len, true);
    const L = lanes(t);
    const cur = (L[param] ??= had ? L[param] : []);
    const inner = pts.map((p) => ({ t: at + p.t, v: p.v }));
    L[param] = dedupe([...cur.filter((p) => p.t < at - 1e-6 || p.t > at + len + 1e-6), ...inner]);
    if (!had) { const before = staticValue(t, param); if (at > 0.001) L[param].unshift({ t: 0, v: before }); }
  }
}

/** Werte im Bereich anheben oder absenken (Trim). Randpunkte halten die Kurve außerhalb fest. */
export function trimRange(t: Track, param: string, s: number, e: number, delta: number) {
  const pts = points(t, param);
  if (!pts.length) { addPoint(t, param, s, staticValue(t, param)); addPoint(t, param, e, staticValue(t, param)); }
  const cur = points(t, param);
  const vs = valueAt(cur, s, param, 0), ve = valueAt(cur, e, param, 0);
  const out = cur.filter((p) => p.t < s - 1e-6 || p.t > e + 1e-6);
  out.push({ t: s, v: clampV(param, vs + delta, t) }, { t: e, v: clampV(param, ve + delta, t) });
  for (const p of cur) if (p.t > s + 1e-6 && p.t < e - 1e-6) out.push({ t: p.t, v: clampV(param, p.v + delta, t) });
  if (s > 0.002) out.push({ t: s - 0.001, v: vs });
  out.push({ t: e + 0.001, v: ve });
  lanes(t)[param] = dedupe(out);
}

/** Punkte weglassen, die sich aus den Nachbarn ergeben (Toleranz in Parametereinheiten) */
export function thin(pts: AutoPoint[], tol: number): AutoPoint[] {
  if (pts.length < 3) return pts;
  const out = [pts[0]];
  for (let i = 1; i < pts.length - 1; i++) {
    const a = out[out.length - 1], b = pts[i + 1], p = pts[i];
    const lin = a.v + ((b.v - a.v) * (p.t - a.t)) / Math.max(1e-9, b.t - a.t);
    if (Math.abs(lin - p.v) > tol || p.t - a.t > 2) out.push(p);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/** Aufgenommenen Durchgang (Touch/Latch/Write) in die Kurve übernehmen: ersetzt den Zeitraum der Aufnahme */
export function writePass(t: Track, param: string, rec: AutoPoint[]) {
  if (rec.length < 1) return;
  const s = rec[0].t, e = rec[rec.length - 1].t;
  const had = points(t, param).length > 0;
  const after = had ? valueAt(points(t, param), e + 0.02, param, 0) : rec[rec.length - 1].v;
  const L = lanes(t);
  const keep = (L[param] ?? []).filter((p) => p.t < s - 1e-6 || p.t > e + 0.02);
  const info = paramInfo(param, t);
  const tol = info.unit === 'dB' ? 0.15 : param === 'pan' ? 0.01 : info.step || info.choices ? 0 : (info.max - info.min) * 0.004;
  // Logarithmische Plugin-Parameter (Frequenzen) im Log-Maß ausdünnen, damit die Kurve in der Anzeige gleich genau bleibt
  const log = info.def?.scale === 'log' && info.min > 0;
  const body = param === 'mute' || info.step || info.choices ? rec.filter((p, i) => i === 0 || p.v !== rec[i - 1].v || i === rec.length - 1)
    : log ? thin(rec.map((p) => ({ t: p.t, v: Math.log(Math.max(info.min, p.v)) })), Math.log(info.max / info.min) * 0.006).map((p) => ({ t: p.t, v: Math.exp(p.v) }))
    : thin(rec, tol);
  const out = [...keep, ...body.map((p) => ({ t: p.t, v: clampV(param, p.v, t) }))];
  // Nach dem Loslassen springt Touch zurück auf die alte Kurve
  if (had) out.push({ t: e + 0.02, v: after });
  if (!had && s > 0.001) out.unshift({ t: 0, v: rec[0].v });
  L[param] = dedupe(out);
}

/** Werte für die Wiedergabe abtasten: Zeitpunkte und Werte ab `from` bis zum letzten Punkt */
export function samples(pts: AutoPoint[], param: string, from: number, stepSec = 0.02): AutoPoint[] {
  if (!pts.length) return [];
  const out: AutoPoint[] = [{ t: from, v: valueAt(pts, from, param, 0) }];
  const last = pts[pts.length - 1].t;
  if (param === 'mute') { for (const p of pts) if (p.t > from) out.push(p); return out; }
  for (let i = 0; i < pts.length; i++) {
    const b = pts[i];
    if (b.t <= from) continue;
    const a = out[out.length - 1];
    // Lautstärke in dB: Zwischenschritte, damit der lineare Gain-Verlauf der dB-Kurve folgt
    if (paramInfo(param).unit === 'dB') for (let x = a.t + stepSec; x < b.t - 1e-6; x += stepSec) out.push({ t: x, v: valueAt(pts, x, param, 0) });
    out.push(b);
  }
  return out.filter((p) => p.t <= last + 1e-6 || p === out[0]);
}
